import React, {useContext, useEffect, useState} from 'react';
import {Row, Col, Button, Spinner} from 'react-bootstrap';
import styles from "./modal-content-order-accept.module.scss";
import SuccessModalContent from "../modal-content-success/modal-content-success";
import {
  ORDER_ACCEPT_SUCCESS, ORDER_ACCEPTED,
  ORDER_ALLOCATION_EXCEEDED_ERROR,
  ORDER_CONFIRM_ERROR
} from "../../../../../helpr/constants";
import ErrorModalContent from "../modal-content-error/modal-content-error";
import {
  REDUCER_ACTION_UPDATE_STATUS,
  SecurityContext,
  SecurityDispatchContext
} from "../../../../../context/security-context";
import SecurityService from "../../../../../services/security-service";
import confirmIcon from "../../../../../assets/confirm-orange.svg"
import AllocationExceededError from "../../../../../models/errors/allocation-exceeded-error";

const OrderAcceptModalContent = ({order, onCancel, onClose, modalConfig}) => {
  //Set states and constant messages
  const SUCCESS_TITLE = "RESERVATION ACCEPTED";
  const ERROR_TITLE = "A PROBLEM OCCURRED";
  const ALLOCATION_ERROR_TITLE = "ALLOCATION EXCEEDED";
  const { setModalState } = modalConfig;
  const [ loading, setLoading] = useState(false);
  const [ page, setPage ] = useState(0);
  const [ errorState, setErrorState] = useState({});
  const { ordersState, securityState } = useContext(SecurityContext);
  const { ordersDispatch } = useContext(SecurityDispatchContext);
  const securityService = new SecurityService();

  //Get updated scaled amount
  const getUpdatedScaledAmount = () => ordersState.filtered.filter(o => o.orderId === order.orderId)[0].scaledAmount;
  //Method for checking if to be accepted amount exceeded the allocation
  const isAllocationExceeded = () => {
    return (ordersState.default.filter(o => o.orderStatus === '1')
      .reduce((total, o) => total + o.scaledAmount, 0) + getUpdatedScaledAmount()) > securityState.allocation;
  }

  useEffect(() => {
    setModalState(prev => {
      return {
        ...prev,
        style: `${styles['confirm-modal']}`
      }
    })
  }, [])

  const acceptOrder = async () => {
    setLoading(true);
    try {
      if(isAllocationExceeded()){
        throw new AllocationExceededError(ORDER_ALLOCATION_EXCEEDED_ERROR);
      }
      await securityService.acceptOrder(securityState.securityId, order.orderId);
      ordersDispatch({
        type: REDUCER_ACTION_UPDATE_STATUS,
        previous: ordersState,
        data: {
          id: order.orderId,
          value: ORDER_ACCEPTED
        }
      });
      setModalState(prev => {
        return {
          ...prev,
          style: `${styles['success-modal']}`
        }
      });
      setPage(1);
    } catch(error) {
      console.log(error);
      if(error instanceof AllocationExceededError){
        setErrorState({
          ...errorState,
          title: ALLOCATION_ERROR_TITLE,
          message: ORDER_ALLOCATION_EXCEEDED_ERROR
        })
      } else {
        setErrorState({
          ...errorState,
          title: ERROR_TITLE,
          message: ORDER_CONFIRM_ERROR
        })
      }
      setModalState(prev => {
        return {
          ...prev,
          style: `${styles['error-modal']}`
        }
      })
      setPage(2)
    } finally {
      setLoading(false);
    }
  }

  const cancel = () => {
    setModalState(prev => {
      return {
        ...prev,
        style: `${styles['success-modal']}`
      }
    });
    onCancel()
  }

  return page === 0 ? (
    <div className={styles['accept-order']}>
      <Row>
        <Col lg={12}>
          <div>
            <h1>Underwriter & Selling Agent’s Certification & Endorsement </h1>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div>
            <img alt="Confirm" src={confirmIcon} />
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div className={styles['message']}>
            <p className={styles['statement']}>
              By clicking <span className={styles['bold']}>ACCEPT</span>, the Underwriter/Selling Agent
              confirms the following declarations, as applicable:
            </p>
            <ol className={styles['alpha']}>
              <li>
                <div>
                  That the Application was received on the e-Securities Issue Portal on the
                  Reservation Date indicated in the Reservation Summary;
                </div>
              </li>
              <li>
                <div>
                  That all the required documents have been received from the Applicant;
                </div>
              </li>
              <li>
                <div>
                  That the necessary know-your-client process was conducted on the Applicant pursuant to the
                  Anti-Money Laundering Act and the amendments thereto ("AMLA") as well as its implementing
                  rules and regulations ("IRR") and our own internal policies, including, but not limited to,
                  doing the KYC on the basis of a third party reliance letter issued by the appropriate institution
                  as allowed by AMLA and its IRR;
                </div>
              </li>
              <li>
                <div>
                  That the identity of the Applicant was duly established pursuant to the AMLA and its IRR;
                </div>
              </li>
              <li>
                <div>
                  That to the best of the Underwriter/Selling Agent’s knowledge, all information provided to the
                  Issuer and the Registrar and Paying Agent regarding the Applicant are true, complete,
                  current and correct;
                </div>
              </li>
              <li>
                <div>
                  That any and all authorizations and waivers from the Applicant necessary for the Underwriter/Selling
                  Agent to disclose all information required by the Issuer and the Registrar and Paying Agent to
                  determine the eligibility of the Applicant have been duly obtained;
                </div>
              </li>
              <li>
                <div>
                  That for the Applicant who indicated to have its securities and/or its clients’ securities registered
                  under PDTC Nominee and lodged under its Depository Account and/or its Clients’ NoCD Accounts under
                  the PDTC Depository Participant, the evidence of the instructions on the purchase of the Security
                  shall be made available upon request of the Issuer, its agent/s and the regulators;
                </div>
              </li>
              <li>
                <div>
                  That the Application to Purchase (ATP) accepted by the Underwriter/Selling Agent, including this
                  Certification and Endorsement, shall be considered valid and binding upon the Underwriter/Selling Agent,
                  and the electronic ATP generated in the e-Securities Issue Portal shall have the same legal
                  effect, validity, and enforceability as a physical ATP with a manually executed signature or electronic
                  signature to the fullest extent permitted by law.
                </div>
              </li>
            </ol>
          </div>
        </Col>
      </Row>
      <Row className={styles['button-row']}>
        <Col xs={6} lg={6}>
          <div className={styles['submit-button']}>
            <Button variant="warning" disabled={loading} onClick={() => acceptOrder()}>
              {loading ? <Spinner size="sm" animation="border"/> : 'ACCEPT'}
            </Button>
          </div>
        </Col>
        <Col xs={6} lg={6}>
          <div className={styles['cancel-button']}>
            <Button variant="secondary" onClick={() => cancel()}>CANCEL</Button>
          </div>
        </Col>
      </Row>
    </div>
  ) : ( page === 1
    ? <SuccessModalContent title={SUCCESS_TITLE} message={ORDER_ACCEPT_SUCCESS} onClose={onClose}/>
    : <ErrorModalContent title={errorState.title} message={errorState.message} onClose={onClose}/>)
}

export default OrderAcceptModalContent;
